import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  Checkbox,
  IconButton,
  TextField,
  InputAdornment,
  LinearProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { getCustomerLead, getTotalLeads } from "../../api";
import moment from "moment";
import SearchedProducts from "./SearchedProducts";
import Layout from "../../components/Layout";

function EnhancedTableHead({ onSelectAllClick, numSelected, rowCount }) {
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{ "aria-label": "select all leads" }}
          />
        </TableCell>
        <TableCell>Name</TableCell>
        <TableCell>Email</TableCell>
        <TableCell>Phone No.</TableCell>
        <TableCell>Lead Value</TableCell>
        <TableCell align="center">Searched Products</TableCell>
        <TableCell>Date</TableCell>
      </TableRow>
    </TableHead>
  );
}

const LeadValue = ({ leads }) => {
  const price = leads.reduce(
    (total, item) => total + item.regularprice * item.quantity,
    0
  );
  return `$${price.toFixed(2)}`;
};

export default function EnhancedTable() {
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [leadsCount, setLeadsCount] = useState(0);
  const [openModel, setOpenModel] = useState(false);
  const [rowData, setRowData] = useState({});
  const [search, setSearch] = useState("");
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    const fetchLeads = async () => {
      setLoading(true);
      try {
        const data = await getCustomerLead(page + 1, search);
        setLeads(data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchLeads();
  }, [page, search]);

  useEffect(() => {
    const fetchTotalLeads = async () => {
      try {
        const data = await getTotalLeads(search);
        setLeadsCount(data.leads_count);
      } catch (error) {
        console.error(error);
      }
    };
    fetchTotalLeads();
  }, [search]);

  const handleSelectAllClick = (event) => {
    setSelected(event.target.checked ? leads.map((n) => n.name) : []);
  };

  const handleModelOpen = useCallback((e, row) => {
    e.stopPropagation();
    setRowData(row);
    setOpenModel(true);
  }, []);

  const handleSearchChange = (e) => setSearchValue(e.target.value);
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(0);
    setSearch(searchValue);
  };

  return (
    <Layout>
      <Box bgcolor="#f0f0f1" minHeight="calc(100vh - 67px)" px={3} py={3}>
        <Typography variant="h5" mb={3}>
          Leads
        </Typography>
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <form onSubmit={handleSearchSubmit}>
            <TextField
              size="small"
              placeholder="Search"
              value={searchValue}
              onChange={handleSearchChange}
              sx={{ width: 250, backgroundColor: "#fff" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton type="submit">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </form>
        </Box>
        <Paper sx={{ width: "100%", mt: 2 }}>
          <TableContainer>
            {loading && <LinearProgress />}
            <Table size="small" aria-label="leads table">
              <EnhancedTableHead
                numSelected={selected.length}
                onSelectAllClick={handleSelectAllClick}
                rowCount={leads.length}
              />
              <TableBody>
                {leads.map((row, index) => (
                  <TableRow key={index} hover>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected.includes(row.name)} />
                    </TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.phone}</TableCell>
                    <TableCell>
                      <LeadValue leads={row.product_searched} />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton onClick={(e) => handleModelOpen(e, row)}>
                        <VisibilityOutlinedIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell>{moment(row.date).format("ll")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {loading && <LinearProgress />}
          </TableContainer>
          <TablePagination
            component="div"
            count={leadsCount}
            rowsPerPage={20}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
          />
        </Paper>
        <SearchedProducts
          openModel={openModel}
          handleClose={() => setOpenModel(false)}
          {...rowData}
        />
      </Box>
    </Layout>
  );
}
